import React, { useEffect, useState } from 'react'
import { Stack, useLocalSearchParams } from 'expo-router'
import { Text, View, useWindowDimensions } from 'react-native'
import { useTheme } from '../../../hooks/useTheme'
import { FixtureProviders } from '../../../preview/visual/FixtureProviders'
import { VISUAL_SCREENS } from '../../../preview/visual/screens'
import cases from '../../../preview/visual/cases.json'
import { TurnPanelConceptStyles } from '../../../preview/TurnPanelConceptStyles'

class VisualErrorBoundary extends React.Component<{ children: React.ReactNode }, { message: string | null }> {
  state = { message: null as string | null }
  static getDerivedStateFromError(error: Error) { return { message: error.message } }
  render() {
    return this.state.message ? <View testID="visual-runtime-error"><Text>{this.state.message}</Text></View> : this.props.children
  }
}

export default function VisualSuiteScreen() {
  const { case: caseId, theme: requestedTheme, turnPanelConcept, interactiveSession } = useLocalSearchParams<{ case: string; theme: string; turnPanelConcept: string; interactiveSession: string }>()
  const scenario = cases.find(item => item.id === caseId)
  const { theme, setTheme } = useTheme()
  const targetTheme = requestedTheme === 'dark' ? 'dark' : 'light'
  const { width, height } = useWindowDimensions()
  const orientation = width > height ? 'landscape' : 'portrait'
  const readinessId = `visual-ready-${caseId}-${targetTheme}-${orientation}`
  const [preparedTheme, setPreparedTheme] = useState<string | null>(null)
  useEffect(() => {
    setPreparedTheme(null)
    setTheme(targetTheme)
    // Native modal windows inherit status-bar flags when they are created.
    // Let the root theme/StatusBar commit before mounting a new screen.
    const timer = setTimeout(() => setPreparedTheme(targetTheme), 500)
    return () => clearTimeout(timer)
  }, [targetTheme, setTheme])
  if (!scenario) return <View testID="visual-runtime-error"><Text>Unknown visual case: {caseId}</Text></View>
  const originalCase = cases.find(item => item.source === scenario.source)!
  const Screen = VISUAL_SCREENS[originalCase.id]!
  return (
    <View style={{ flex: 1 }} className="bg-canvas" testID={`visual-case-${caseId}`}>
      <Stack.Screen options={{ headerShown: false, animation: 'none' }} />
      <TurnPanelConceptStyles conceptId={turnPanelConcept} dark={theme === 'dark'} />
      {(theme === targetTheme || interactiveSession) && preparedTheme === targetTheme && <FixtureProviders key={`${caseId}-${requestedTheme}-${interactiveSession ?? ''}`} scenario={scenario} interactiveSession={interactiveSession} readinessId={readinessId}>
        <VisualErrorBoundary key={`${caseId}-${requestedTheme}`}><Screen /></VisualErrorBoundary>
      </FixtureProviders>}
    </View>
  )
}
